import { EventEmitter } from '@angular/core';

import { AirportSearchResult } from '../../airport/models/airport-search-result.model';
import { QueryValidationResult } from '../models/query-validation-result.model';


/**
 * Encoded filter value type that allows either a single value (e.g., public=1) or multiple (e.g.,
 * fuel=100LL&fuel=Jet-A). Note the value does not need to be URL-encoded.
 */
export declare type ENCODED_VALUE = string|string[];


/**
 * Enumeration of all possible filter keys for airports and destinations. The enum values are used to encode the
 * search criteria in the URL, so they should be unique, concise, and descriptive.
 */
export enum FilterKeys {
  FuelFilter = 'fuel',
  PublicUse = 'public',
  Towered = 'towered',
  NoLandingFee = 'nolandingfee',
  CourtesyCar = 'courtesycar',
  RentalCar = 'rentalcar',
  Customs = 'customs',
  Oxygen = 'oxygen',
  Restaurant = 'restaurant',
  Camping = 'camping',
  PavedRunway = 'paved',
  Helipad = 'helipad',
  Waterway = 'waterway',
  Grass = 'grass',
  RunwayLength = 'rwylen',
  Approach = 'approach',
  Services = 'services'
}


/**
 * Filter configuration type for defining a new filter.
 */
export interface FilterConfig {
  /**
   * Unique key for this filter that allows for lookup. The key is also used for encoding the filter in the URL.
   */
  key: FilterKeys;

  /**
   * Indicates if multiple instances of this filter are allowed in a single query. For instance, the fuel filter may
   * appear multiple times (fuel=100LL&fuel=Jet-A), resulting in returning airports that have all of the specified
   * fuel types available.
   */
  multi?: boolean;

  /**
   * Indicates if the filter is initially enabled.
   */
  enabled?: boolean;

  /**
   * Function to execute to determine if a given search result passes the filter.
   * @param AirportSearchResult Search result to test
   * @returns {boolean} True if the search result satisfies the filter; false otherwise.
   */
  apply: (AirportSearchResult) => boolean;

  /**
   * Function to execute to encode this filter in the URL as a query parameter. The name of the query parameter is
   * determined by the filter key, and the value is determined by the result of this function. There is no need to
   * URL-encode the value.
   * @returns {string}
   */
  encode: () => string;

  /**
   * Function to execute to decode this filter from the URL. The function should return the enabled state of the
   * filter, and also set all appropriate properties on the filter instance.
   * @param ENCODED_VALUE Encoded value of the filter as it appears in the URL.
   * @returns {boolean} True if the filter should be enabled; false otherwise.
   */
  decode: (ENCODED_VALUE) => boolean;

  /**
   * Validates the filter. If a filter is invalid, it will not be executed even if it is enabled.
   * @returns {QueryValidationResult}
   */
  validate: () => QueryValidationResult;
}


/**
 * Search filter class. This class contains the logic for validating and applying the filter against a search result.
 */
export class Filter {
  /**
   * Indicates if the filter is enabled. If the filter is not enabled, it will always pass.
   */
  enabled: boolean;

  private _key: FilterKeys;
  private _multi: boolean;
  private _applyFn: (AirportSearchResult) => boolean;
  private _encodeFn: () => string;
  private _decodeFn: (ENCODED_VALUE) => boolean;
  private _validateFn: () => QueryValidationResult;

  /**
   * Unique key for this filter that allows for lookup. The key is also used for encoding the filter in the URL.
   */
  get key(): FilterKeys {
    return this._key;
  }

  /**
   * Indicates if multiple instances of this filter are allowed in a single query. For instance, the fuel filter may
   * appear multiple times (fuel=100LL&fuel=Jet-A), resulting in returning airports that have all of the specified
   * fuel types available.
   */
  get multi(): boolean {
    return this._multi;
  }

  /**
   * Returns true if the filter is valid.
   */
  get valid(): boolean {
    return this.validate().valid;
  }

  constructor(filterConfig: FilterConfig) {
    this._key = filterConfig.key;
    this._multi = filterConfig.multi;
    this._applyFn = filterConfig.apply;
    this._encodeFn = filterConfig.encode;
    this._decodeFn = filterConfig.decode;
    this._validateFn = filterConfig.validate;
    this.enabled = filterConfig.enabled;
  }

  /**
   * Execute the filter to determine if a given search result passes the filter.
   * @param airport {AirportSearchResult} Search result to test
   * @returns {boolean} True if the search result satisfies the filter; false otherwise.
   */
  apply(airport: AirportSearchResult): boolean {
    return !this.enabled || this._applyFn(airport);
  }

  /**
   * Encode the value of this filter in the URL as a query parameter.
   * @returns {string}
   */
  encode(): string {
    return this._encodeFn();
  }

  /**
   * Decodes the filter value from the URL
   * @param v {ENCODED_VALUE} Encoded value of the filter as it appears in the URL.
   * @returns {boolean} True if the filter should be enabled; false otherwise.
   */
  decode(v: ENCODED_VALUE): boolean {
    return this._decodeFn(v);
  }

  /**
   * Validates the filter. If a filter is invalid, it will not be executed even if it is enabled.
   * @returns {QueryValidationResult}
   */
  validate(): QueryValidationResult {
    return this._validateFn();
  }
}


/**
 * Configuration object for boolean filters.
 */
export interface BooleanFilterConfig {
  /**
   * Unique key for this filter that allows for lookup. The key is also used for encoding the filter in the URL.
   */
  key: FilterKeys;

  /**
   * Indicates if the filter is initially enabled.
   */
  enabled?: boolean;

  /**
   * Function to execute to determine if a given search result passes the filter.
   * @param AirportSearchResult Search result to test
   * @returns {boolean} True if the search result satisfies the filter; false otherwise.
   */
  apply: (AirportSearchResult) => boolean;
}


/**
 * Boolean filter class. Boolean filters use the enabled property as the value.
 */
export class BooleanFilter extends Filter {
  constructor(filterConfig: BooleanFilterConfig) {
    super(Object.assign({
      encode: () => this._encode(),
      decode: (v: ENCODED_VALUE) => this._decode(v),
      validate: () => this._validate()
    }, filterConfig));
  }

  private _encode(): string {
    return this.enabled ? '1' : null;
  }

  private _decode(v: ENCODED_VALUE): boolean {
    return v === '1';
  }

  private _validate(): QueryValidationResult {
    return { valid: true };
  }
}


/**
 * Base class for filter components.
 */
export abstract class FilterComponentBase {
  /**
   * The underlying filter instance.
   */
  filter: Filter;

  /**
   * Event emitter that fires whenever the filter value changes.
   */
  abstract change: EventEmitter<any>;
}


/**
 * Interface that all filter list components must implement.
 */
export interface IFilterList {
  /**
   * Returns the number of filters currently selected.
   */
  getNumSelected(): number;
}
